import { featureFlag, useFeatureFlag } from '@routable/feature-configs';
import { createItemFormFields, formNamesItem, PaymentDeliveryMethodType } from '@routable/shared';
import dayjs from 'dayjs';
import _cloneDeep from 'lodash/cloneDeep';
import _debounce from 'lodash/debounce';
import _get from 'lodash/get';
import _set from 'lodash/set';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePrevious } from 'react-use';
import { change, formValueSelector } from 'redux-form';
import { ItemKinds } from 'constants/item';
import { useAnnotations } from 'helpers/ocr';
import { getQueryParam } from 'helpers/queryParams';
import useSelectorWithProps from 'hooks/useSelectorWithProps';
import { createItemFormUiFieldMetaSelector } from 'queries/createItemFormSelectors';
import { primaryPartnershipFundingAccountsForPartnershipIdFromCreateItemFormSelector } from 'queries/partnershipCompoundSelectors';
import { createItemFormUiSelectedCompanySelector } from 'selectors/forms';
import { useItemInitialDataFromOverlays } from '../useItemInitialDataFromOverlays';
import { ocrSourceToTextMap } from './constants';
export const DEFAULT_TEXT = 'Default for this vendor';
export const LAST_USED_TEXT = 'Used on last bill for this vendor';
export const useOverlaysSourceForFieldValue = (fieldPath = '') => {
    const itemId = getQueryParam('id');
    const dispatch = useDispatch();
    const isPredictiveCodingUiEnabled = useFeatureFlag(featureFlag.Enum.predictive_coding_inbox_ui_nonfunctional);
    const primaryPartnershipFundingAccount = useSelector(primaryPartnershipFundingAccountsForPartnershipIdFromCreateItemFormSelector);
    const selectedCompany = useSelector(createItemFormUiSelectedCompanySelector);
    const { annotationData, isOcrEnabled, isInboxBillView } = useAnnotations(itemId);
    const isDataSourceEnabled = Boolean(isOcrEnabled && isInboxBillView && itemId);
    const setFieldsMeta = useCallback((path) => dispatch(change(formNamesItem.CREATE_ITEM, `${createItemFormFields.UI_FIELDS}.${path}`, true)), [dispatch]);
    const wasFieldUpdated = useSelectorWithProps(createItemFormUiFieldMetaSelector, fieldPath);
    const fieldFormValue = useSelectorWithProps(formValueSelector(formNamesItem.CREATE_ITEM), fieldPath);
    const fieldFormVal = fieldPath === createItemFormFields.SELECTED_COMPANY ? selectedCompany : fieldFormValue;
    const refValOrVal = fieldFormVal?.ref ? fieldFormVal.ref : fieldFormVal;
    const fieldValue = fieldFormVal?.id ? fieldFormVal.id : refValOrVal;
    const prevValue = usePrevious(fieldValue);
    useEffect(() => {
        const isPrevValueDefined = prevValue !== undefined && prevValue !== null;
        if (isDataSourceEnabled && !wasFieldUpdated && isPrevValueDefined) {
            const isFieldUpdated = typeof fieldValue === 'number' || typeof prevValue === 'number'
                ? parseFloat(String(fieldValue)) !== parseFloat(String(prevValue))
                : fieldValue !== prevValue;
            if (isFieldUpdated) {
                if (fieldPath === createItemFormFields.UI_DATE_SCHEDULED_TYPE) {
                    setFieldsMeta(createItemFormFields.ITEM_DATE_SCHEDULED);
                }
                setFieldsMeta(fieldPath);
            }
        }
    }, [fieldPath, fieldValue, isDataSourceEnabled, setFieldsMeta, wasFieldUpdated]);
    const onSelectSource = (sourceVal) => {
        dispatch(change(formNamesItem.CREATE_ITEM, fieldPath, sourceVal));
    };
    const { dataWithSources } = useItemInitialDataFromOverlays({
        enabled: isDataSourceEnabled,
        kind: ItemKinds.PAYABLE,
        itemId,
    });
    if (!isDataSourceEnabled || wasFieldUpdated) {
        return {
            currentSource: undefined,
            isPredictiveCodingUiEnabled,
            onSelectSource,
            sources: [],
        };
    }
    const sharedProps = {
        isPredictiveCodingUiEnabled,
        onSelectSource,
        sources: [],
    };
    if (fieldPath === createItemFormFields.SELECTED_COMPANY) {
        const partnershipIdFromAnnotation = annotationData?.partnership?.id || annotationData?.item?.predictedPayeePartnership?.id;
        const partnershipNameFromAnnotation = annotationData?.partnership?.name ||
            annotationData?.item?.predictedPayeePartnership?.name ||
            annotationData?.item?.predictedPayeeName;
        let currentSource;
        if ((partnershipIdFromAnnotation && selectedCompany?.id === partnershipIdFromAnnotation) ||
            (partnershipNameFromAnnotation && selectedCompany?.id === partnershipNameFromAnnotation)) {
            currentSource = ocrSourceToTextMap.ocr;
        }
        return { currentSource, ...sharedProps };
    }
    if (fieldPath === createItemFormFields.ITEM_PAYMENT_DELIVERY_METHOD) {
        let currentSource;
        if (fieldValue === selectedCompany?.defaultPayablePaymentRail?.deliveryMethod) {
            currentSource = LAST_USED_TEXT;
        }
        else if (fieldValue !== PaymentDeliveryMethodType.ANY) {
            currentSource = DEFAULT_TEXT;
        }
        return { currentSource, ...sharedProps };
    }
    if (fieldPath === createItemFormFields.ITEM_CURRENCY_CODE_RECEIVER) {
        return {
            currentSource: DEFAULT_TEXT,
            ...sharedProps,
        };
    }
    if (fieldPath === createItemFormFields.ITEM_PAYMENT_DELIVERY_OPTION) {
        const currentSource = fieldValue === selectedCompany?.defaultPayablePaymentRail?.deliveryOption ? LAST_USED_TEXT : DEFAULT_TEXT;
        return { currentSource, ...sharedProps };
    }
    if (fieldPath === createItemFormFields.ITEM_FUNDING_ACCOUNT_ID) {
        const currentSource = fieldValue === selectedCompany?.defaultPayableFundingAccount?.id ? LAST_USED_TEXT : DEFAULT_TEXT;
        return { currentSource, ...sharedProps };
    }
    if (fieldPath === createItemFormFields.ITEM_PARTNER_RECEIVABLE_ACCOUNT) {
        const currentSource = fieldValue === primaryPartnershipFundingAccount?.id ? DEFAULT_TEXT : LAST_USED_TEXT;
        return { currentSource, ...sharedProps };
    }
    if (fieldPath === createItemFormFields.UI_DATE_SCHEDULED_TYPE ||
        fieldPath === createItemFormFields.ITEM_DATE_SCHEDULED) {
        return {
            currentSource: 'Default for this workspace',
            ...sharedProps,
        };
    }
    const accountItemIdFieldPath = fieldPath.replace(/\.account$/g, '.account_id').replace(/\.item$/g, '.item_id');
    const sources = _get(dataWithSources, fieldPath) || _get(dataWithSources, accountItemIdFieldPath) || [];
    const sourceObj = sources.find(({ value }) => {
        if (typeof value === 'object' && value !== null) {
            return ('id' in value && value.id === fieldValue) || ('ref' in value && value.ref === fieldValue);
        }
        if (typeof value === 'number' || typeof fieldValue === 'number') {
            return parseFloat(String(value)) === parseFloat(String(fieldValue));
        }
        if (typeof value === 'string') {
            return value === fieldValue || dayjs(value).isSame(dayjs(fieldValue));
        }
        return value === fieldValue;
    });
    return {
        currentSource: ocrSourceToTextMap[sourceObj?.source],
        isPredictiveCodingUiEnabled,
        onSelectSource,
        sources,
    };
};
